import { isInExcludedLegalEntity } from "_shared/_views/tables/row-choice-excluded-legal-entity";
import EntityMember from "_shared/entity-members/entity-member";
import {
  FeeScheduleTemplateCategory,
  withFeeScheduleTeamMember,
  withoutFeeScheduleTeamMember,
} from "_shared/fee-schedules/categories/fee-schedule-template-category";
import Guid from "_shared/guids/guid";

/**
 * Applying a fee schedule template to a proposal carries who each rate is for. A template belongs
 * to a firm and names anyone in it; a proposal's rates may only name somebody on that proposal's
 * vendor team — `WorkAgreement.ValidateFeeSchedule` refuses to hire otherwise. So a template can
 * always name somebody the proposal has never heard of, and this is the arithmetic of that gap.
 *
 * Nothing here decides what to do about it. `useReconcileFeeScheduleStaffing` asks the user, and
 * the issue that asked for this (#5237) is explicit that neither outcome may happen silently.
 */

/** Whether this person is already on the proposal's team. */
function isOnTeam(person: EntityMember, teamMembers: readonly EntityMember[]): boolean {
  return teamMembers.some((member) => member.isEqualTo(person));
}

/**
 * The people these categories name who cannot bill them as they stand, each listed once: anybody
 * not on the team, and anybody from `excludedLegalEntityId` — the client's own firm, which bills no
 * rate of its own even when one of its people sits on the vendor team (#6091).
 *
 * Empty means the template can be applied as it stands, which is the common case and the one that
 * must not stop to ask anything.
 */
export function staffingNotOnTeam(
  categories: readonly FeeScheduleTemplateCategory[],
  teamMembers: readonly EntityMember[],
  excludedLegalEntityId?: Guid,
): EntityMember[] {
  const strangers: EntityMember[] = [];
  for (const category of categories) {
    const person = category.teamMember;
    if (!person) continue;
    if (isOnTeam(person, teamMembers) && !isInExcludedLegalEntity(excludedLegalEntityId, person.legalEntityId)) continue;
    if (strangers.some((stranger) => stranger.isEqualTo(person))) continue;
    strangers.push(person);
  }
  return strangers;
}

/**
 * The same categories, with only the names the team can account for.
 *
 * This is the "drop them from the rate" outcome. A rate whose person is dropped keeps its work and
 * its price and simply names nobody, which is what every rate a template supplied did before a
 * template could name anyone.
 */
export function withStaffingLimitedToTeam<T extends FeeScheduleTemplateCategory>(
  categories: readonly T[],
  teamMembers: readonly EntityMember[],
): T[] {
  return categories.map((category) =>
    category.teamMember && !isOnTeam(category.teamMember, teamMembers)
      ? withoutFeeScheduleTeamMember(category)
      : category,
  );
}

/** What the user chose to do about one person a template names who is not on the team. */
export type FeeScheduleStaffingDecision =
  | { kind: "add" }
  | { kind: "substitute"; substitute: EntityMember }
  | { kind: "unassign" };

/**
 * How a person is keyed while the user decides about them.
 *
 * Both ids, because that is what `isOnTeam` compares and what the server compares
 * (`WorkAgreement.ValidateFeeSchedule`). Keying on the user id alone would let one person holding
 * seats at two entities collapse into a single row, and a decision about one would silently answer
 * for the other.
 */
export function feeScheduleStaffingKey(person: EntityMember): string {
  return `${person.userId.value}:${person.legalEntityId.value}`;
}

/**
 * The categories and the team additions that a set of per-person decisions add up to.
 *
 * A person with no decision is left exactly as they are, which is the right answer for anyone who
 * was already on the team — they were never asked about.
 *
 * A substitute is only added to the team when the team does not already have them. Handing back
 * somebody who is already seated would be a no-op on a draft and a pointless vendor team redline on
 * an active proposal, which the counterparty would then have to read and dismiss.
 */
export function withStaffingDecisionsApplied<T extends FeeScheduleTemplateCategory>(
  categories: readonly T[],
  teamMembers: readonly EntityMember[],
  decisions: ReadonlyMap<string, FeeScheduleStaffingDecision>,
): { categories: T[]; membersToAddToTeam: EntityMember[] } {
  const membersToAddToTeam: EntityMember[] = [];
  const addMember = (member: EntityMember) => {
    if (isOnTeam(member, teamMembers)) return;
    if (membersToAddToTeam.some((added) => added.isEqualTo(member))) return;
    membersToAddToTeam.push(member);
  };

  const decided = categories.map((category) => {
    const person = category.teamMember;
    if (!person) return category;
    const decision = decisions.get(feeScheduleStaffingKey(person));
    if (!decision) return category;
    if (decision.kind === "unassign") return withoutFeeScheduleTeamMember(category);
    if (decision.kind === "substitute") {
      addMember(decision.substitute);
      return withFeeScheduleTeamMember(category, decision.substitute);
    }
    addMember(person);
    return category;
  });

  return { categories: decided, membersToAddToTeam };
}

/**
 * Of the people a staffing answer would seat, those who bill one of `categories`.
 *
 * The answer is given for every rate the user was asked about, but not every one of them is added
 * in the end: one whose name a collaborator put on the fee schedule while the question was open is
 * left out, and so is one the user chose not to overwrite. Somebody whose every rate was left out is
 * not seated, or they would join the team for no rate at all.
 */
export function staffingForRates(
  membersToAddToTeam: readonly EntityMember[],
  categories: readonly FeeScheduleTemplateCategory[],
): EntityMember[] {
  return membersToAddToTeam.filter((member) =>
    categories.some((category) => category.teamMember?.isEqualTo(member)),
  );
}
